import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
  AssignmentCustomerResponse,
  AssignmentPspResponse,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  baseAuthSelectors,
  BaseButtonSize,
  BaseCoreState,
  BaseFormatDatePipe,
  BaseModalService,
  BaseSignatureService,
  BaseTokenInterface,
  ContractResponse,
  BaseDateService,
  BaseIntervalStatusType,
  BaseIntervalStatus,
  BaseSigningStatus,
} from 'base';
import { ExternalEmployeeService } from '../../../modules/external-employee/external-employee.service';

@Component({
  selector: 'base-customer-employee-operation-info-tab',
  templateUrl: './base-customer-employee-operation-info-tab.component.html',
})
export class BaseCustomerEmployeeOperationInfoTabComponent implements OnInit, OnDestroy {
  @Output() signContract: EventEmitter<any> = new EventEmitter<any>();
  @Output() deregisterEmployee: EventEmitter<any> = new EventEmitter<any>();
  @Output() inquireEmployee: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  buttonSize = BaseButtonSize;
  signatureStatus = BaseSigningStatus;
  assignment: AssignmentCustomerResponse | undefined;
  assignmentStatus: BaseIntervalStatusType;
  documentViewerModalRef: NgbModalRef | undefined;
  contractFile: { data: Blob | undefined; fileName: string | undefined } | undefined;
  loggedInUserId: number;
  ownSignatureStatus: string;
  partnerSignatureStatus: string;
  ownSignatureStatusParams: any;
  partnerSignatureStatusParams: any;
  validContract: ContractResponse | undefined;
  intervalStatus = BaseIntervalStatus;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private translateService: TranslateService,
    private formatDatePipe: BaseFormatDatePipe,
    private router: Router,
    private baseSignatureService: BaseSignatureService,
    private externalEmployeeService: ExternalEmployeeService
  ) {}

  ngOnInit(): void {
    this.store
      .select(baseAssignmentsSelectors.getDocumentView)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data)
      )
      .subscribe((document) => {
        this.contractFile = document;

        if (!this.documentViewerModalRef) {
          this.openDocumentViewer();
        }
      });

    combineLatest([
      this.store.select(baseAssignmentsSelectors.getAssignment),
      this.store.select(baseAuthSelectors.getUserInfo),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([assignmentState, userState]) => !!assignmentState.ok && !!userState.ok),
        map(
          ([assignmentState, userState]) =>
            [assignmentState.data, userState.data] as [
              AssignmentPspResponse | AssignmentCustomerResponse,
              BaseTokenInterface
            ]
        )
      )
      .subscribe(([assignment, user]) => {
        this.loggedInUserId = user.id;
        this.assignment = assignment as AssignmentCustomerResponse;
        this.assignmentStatus = BaseDateService.getIntervalStatus(this.assignment.startDate, this.assignment.endDate);

        this.validContract = this.assignment.contracts?.find((contract) => contract.valid) as ContractResponse;
        this.setAssignmentSignatureStatus();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  viewContract(): void {
    if (!this.assignment || !this.validContract) {
      return;
    }

    this.store.dispatch(
      baseAssignmentsActions.viewContract({
        payload: { assignment: this.assignment.id, contract: this.validContract.id },
      })
    );
  }

  onSignContractClick(event: Event): void {
    this.signContract.emit(event);
  }

  onDeregisterEmployeeClick(): void {
    this.deregisterEmployee.emit(true);
  }

  reloadAssignment(): void {
    if (!this.assignment) {
      return;
    }

    this.store.dispatch(baseAssignmentsActions.loadAssignment({ payload: { assignment: this.assignment.id } }));
  }

  openIzsInfoModal(event: Event): void {
    if (!this.assignment) {
      return;
    }

    event.stopPropagation();
    this.modalService.closeAllModals();
    this.router.navigate(['/service-provider'], {
      queryParams: {
        page: 1,
        search: this.assignment.pspBranch.name,
        pspBranchId: this.assignment.pspBranch.id,
        location: this.assignment.customerLocation.id,
      },
    });
  }

  openInquireEmployeeModal(): void {
    this.inquireEmployee.emit();
  }

  private openDocumentViewer(): void {
    if (this.baseSignatureService.baseSignContractsInfoCardIsInitSubject.value) {
      return;
    }

    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'lg' }
    );
    this.documentViewerModalRef.result.then(
      () => this.cleanDocumentViewer(),
      () => this.cleanDocumentViewer()
    );
  }

  private setAssignmentSignatureStatus(): void {
    this.ownSignatureStatus =
      this.validContract?.signatures?.statusCustomer === this.signatureStatus.signed
        ? 'externalEmployees.contractStatus.signedByOwnSide'
        : this.validContract?.signatures?.statusCustomer === this.signatureStatus.in_queue
        ? 'externalEmployees.contractStatus.ownSideInQueue'
        : this.validContract?.signatures?.statusCustomer === this.signatureStatus.error
        ? 'externalEmployees.contractStatus.ownSideError'
        : 'externalEmployees.contractStatus.notSignedByOwnSide';

    this.ownSignatureStatusParams =
      this.validContract?.signatures?.statusCustomer === this.signatureStatus.unsigned
        ? {}
        : {
            signedAt: this.formatDatePipe.transform(this.validContract?.signedAtCustomer),
            signingMethod: this.translateService.instant(
              `externalEmployees.contractSignMethod.${this.validContract?.type}`
            ),
            signedBy:
              this.validContract?.signedByCustomerUser?.id === this.loggedInUserId
                ? this.translateService.instant('label.you')
                : `${this.validContract?.signedByCustomerUser?.firstName} ${this.validContract?.signedByCustomerUser?.lastName}`,
          };

    this.partnerSignatureStatus =
      this.validContract?.signatures?.statusPsp === this.signatureStatus.signed
        ? 'externalEmployees.contractStatus.signedByPartner'
        : 'externalEmployees.contractStatus.notSignedByPartner';

    this.partnerSignatureStatusParams =
      this.validContract?.signatures?.statusPsp === this.signatureStatus.signed
        ? {
            signedAt: this.formatDatePipe.transform(this.validContract?.signedAtPSP),
            signingMethod: this.translateService.instant(
              `externalEmployees.contractSignMethod.${this.validContract.type}`
            ),
            signedBy: `${this.validContract?.signedByPspUser?.firstName} ${this.validContract?.signedByPspUser?.lastName}`,
          }
        : {};
  }

  private cleanDocumentViewer(): void {
    this.store.dispatch(baseAssignmentsActions.clearDocumentView());
    this.documentViewerModalRef = undefined;
  }
}
